import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Rocket, ArrowLeft, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Forgot password states
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetStep, setResetStep] = useState('email'); // email, token, success
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);

    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });

      const data = await response.json();
      
      if (response.ok) {
        if (data.reset_token) {
          setResetToken(data.reset_token);
          setResetStep('token');
        } else {
          toast.success('If an account exists, a reset code has been sent.');
          setResetStep('token');
        }
      } else {
        toast.error(data.detail || 'Failed to request password reset');
      }
    } catch (error) {
      toast.error('Failed to request password reset');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setResetLoading(true);

    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          token: resetToken, 
          new_password: newPassword 
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setResetStep('success');
        toast.success('Password reset successfully!');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(resetToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeForgotPassword = () => {
    setForgotPasswordOpen(false);
    setResetEmail('');
    setResetToken('');
    setResetStep('email');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="min-h-screen bg-[#050508] flex flex-col items-center justify-center p-6">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a15] via-[#050508] to-[#050508] opacity-50" />
      
      <div className="relative z-10 w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full gradient-primary flex items-center justify-center glow-soft">
            <Rocket className="w-10 h-10 text-black" strokeWidth={1.5} />
          </div>
          <h1 className="text-gradient text-2xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Callen's Mission
          </h1>
          <p className="text-white/50 text-sm">Mission Control Platform</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-white/60 text-sm mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-modern w-full"
              placeholder="Enter your email"
              required
              data-testid="login-email"
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-modern w-full"
              placeholder="Enter your password"
              required
              data-testid="login-password"
            />
          </div>

          <div className="text-right">
            <button
              type="button"
              onClick={() => setForgotPasswordOpen(true)}
              className="text-[#00f3ff]/70 hover:text-[#00f3ff] text-sm transition-colors"
              data-testid="forgot-password-btn"
            >
              Forgot Password?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3 disabled:opacity-50"
            data-testid="login-submit"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p className="text-center text-white/40 text-sm mt-6">
          Don't have an account?{' '}
          <Link to="/signup" className="text-[#00f3ff] hover:underline">
            Sign up
          </Link>
        </p>

        <p className="text-center text-white/30 text-xs mt-8">
          This Mission Control is your private space.
        </p>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={closeForgotPassword}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white text-lg font-semibold">
              {resetStep === 'email' && 'Forgot Password'}
              {resetStep === 'token' && 'Reset Your Password'}
              {resetStep === 'success' && 'Password Reset!'}
            </DialogTitle>
          </DialogHeader>

          {resetStep === 'email' && (
            <form onSubmit={handleForgotPassword} className="space-y-4 py-4">
              <p className="text-white/60 text-sm">
                Enter your email address and we'll generate a reset code for you.
              </p>
              <div>
                <label className="block text-white/60 text-sm mb-2">Email</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="input-modern w-full"
                  placeholder="Enter your email"
                  required
                  data-testid="reset-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={resetLoading}
                className="btn-primary w-full py-3 disabled:opacity-50"
                data-testid="request-reset-btn"
              >
                {resetLoading ? 'Requesting...' : 'Get Reset Code'}
              </button>
            </form>
          )}

          {resetStep === 'token' && (
            <form onSubmit={handleResetPassword} className="space-y-4 py-4">
              {resetToken && (
                <div className="p-4 rounded-xl bg-[#00f3ff]/10 border border-[#00f3ff]/30">
                  <p className="text-white/60 text-xs mb-2">Your Reset Code:</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[#00f3ff] text-2xl font-mono font-bold tracking-wider">
                      {resetToken}
                    </span>
                    <button
                      type="button"
                      onClick={copyToken}
                      className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      {copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} className="text-white/40" />}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-white/60 text-sm mb-2">Reset Code</label>
                <input
                  type="text"
                  value={resetToken}
                  onChange={(e) => setResetToken(e.target.value.toUpperCase())}
                  className="input-modern w-full text-center text-lg font-mono tracking-widest"
                  placeholder="XXXXXXXX"
                  required
                  data-testid="reset-token-input"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-2">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-modern w-full"
                  placeholder="Enter new password"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-2">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-modern w-full"
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                />
              </div>

              <button
                type="submit"
                disabled={resetLoading}
                className="btn-primary w-full py-3 disabled:opacity-50"
                data-testid="reset-password-btn"
              >
                {resetLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          {resetStep === 'success' && (
            <div className="py-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-white mb-4">Your password has been reset successfully!</p>
              <button
                onClick={closeForgotPassword}
                className="btn-primary py-3 px-8"
              >
                Back to Login
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
